import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { useMediaQuery } from 'react-responsive';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { Alert, Badge, Button, Column, Container, Icon, Modal, Row, Select, TextInput } from '@pella/sash-framework';
import { Layout, Breadcrumbs, LoadingOverlay, Image, CTA } from '../../../';
import { getLocalStorage, getSessionStorage, removeLocalStorage, removeSessionStorage, setSessionStorage, monetateDataLayer, setCookie, getCookie } from '../../../utilities/functions';
import { setTag, getCurrentScope, setContext, captureMessage } from '@sentry/gatsby';
import { useRtaData } from '../../../../hooks/use-rta-data';
import { useOktaAuth } from '../../../../hooks/use-okta-auth';
import { getVisibleGlassMeasurements } from '../../../page/shop/calculate-measurements/visible-glass-util';
import AddToProject from '../../../page/shop/common/add-to-project';
import ImageViewer from '../../../page/shop/common/image-viewer';
import AddToCart from '../../../page/shop/common/add-to-cart';
import ExitIntent from '../../../page/shop/common/exit-intent';
import ScheduleConsultation from '../../../page/rta/v2/home';
import Price from '../../../page/shop/common/price';
import CardRenderer from '../../../common/card-types/card-renderer';
import CalculateMeasurements from '../../../page/shop/calculate-measurements';
import CalculatorResults from '../../../page/shop/calculate-measurements/results';
import styles from './configuration.module.scss';
import { isValidArray, isValidPostal } from '../../../utilities/validations';
import { useProduct } from '../../../../hooks/use-product';
import { useLocation } from '../../../../context/LocationContext';

const Configuration = (props) => {
	const queryData = useStaticQuery(query);
	const configData = queryData?.contentstackSummaryFields;
	const locationConfig = queryData?.contentstackLocationConfig;
	const measureConfig = queryData?.contentstackConfiguratorMeasure;
	const questionsConfig = queryData?.contentstackQuestionsAnswers;
	const { loadItem, getLineItemConfig, updateItem, reconfigure, updateDrawing, getCurrentConfigurationState } = useProduct();
	const rtaData = useRtaData();
	const errorData = configData.errors;
	const locations = useSelector((state) => state.locations);
	const { isLogged, profile } = useOktaAuth();
	const { setLocation } = useLocation();
	const image350 = useMediaQuery({ query: '(min-width: 449px)' });
	const image400 = useMediaQuery({ query: '(min-width: 959px)' });
	const image450 = useMediaQuery({ query: '(min-width: 1190px)' });
	const [loading, setLoading] = useState(true);
	const [loadConfiguration, setLoadConfiguration] = useState(false);
	const [isEcommerce, setIsEcommerce] = useState(null);
	const [isPro, setIsPro] = useState(null);
	const [clientID, setClientID] = useState('H03');
	const [configKey, setConfigKey] = useState(props.location?.state?.configKey || '');
	const [error, setError] = useState('');
	const [editItem, setEditItem] = useState(null);
	const [currentItem, setCurrentItem] = useState({});
	const [pageLoading, setPageLoading] = useState(true);
	const [gettingInitialConfig, setGettingInitialConfig] = useState(false);
	const [productID, setProductID] = useState((typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('id')) ?? null);
	const [configOptions, setConfigOptions] = useState((typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('options')) ?? null);
	const [itemHeading, setItemHeading] = useState(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('heading'));
	const [productType, setProductType] = useState((typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('type')) ?? null);
	const [productBrand, setProductBrand] = useState((typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('brand')) ?? null);
	const [productName, setProductName] = useState(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('name'));
	const [pdpURL, setPdpURL] = useState(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('pdpURL'));
	const [currentDrawing, setCurrentDrawing] = useState({});
	const [drawings, setDrawings] = useState({});
	const [drawingView, setDrawingView] = useState('');
	const [updatingDrawing, setUpdatingDrawing] = useState(false);
	const [imageDisclaimer, setImageDisclaimer] = useState(null);
	const [limitations, setLimitations] = useState(null);
	const [unorderedQuestions, setUnorderedQuestions] = useState([]);
	const [questionError, setQuestionError] = useState(false);
	const [itemEdited, setItemEdited] = useState(false);
	const [measurementQuestions, setMeasurementQuestions] = useState([]);
	const [questionOrder, setQuestionOrder] = useState([]);
	const [questions, setQuestions] = useState([]);
	const [modalOpen, setModalOpen] = useState(null);
	const [isPartsOnly, setIsPartsOnly] = useState(false);
	const [drawingSize, setDrawingSize] = useState(0);
	const measurements = measureConfig?.measure[measureConfig.measure.findIndex((measureType) => measureType.product_type.includes(productType))];
	const [itemQty, setItemQty] = useState(1);
	const [zipValue, setZipValue] = useState(null);
	const [modalError, setModalError] = useState(null);
	const [branchRegion, setBranchRegion] = useState(null);
	const [stockQty, setStockQty] = useState(null);
	const [incrementUnit, setIncrementUnit] = useState(0.125);
	const [userModification, setUserModification] = useState(false);
	const [page_links, setPage_Links] = useState([]); // breadcrumbs
	const [queryParamsError, setQueryParamsError] = useState(false);
	const [isEastCoast, setIsEastCoast] = useState(true);
	const isMulled = currentItem?.current_step_data?.questions?.filter((question) => question.question_text === 'Overall Frame Width')?.length ? true : false;
	const pageTitle = 'eCom Configuration';

	useEffect(() => {
		if (productID !== null && Object.keys(currentItem).length > 0) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'configure',
				ecommerce: {
					click: {
						products: [
							{
								id: currentItem?.mpn || productID,
								name: productID,
							},
						],
					},
				},
			});
		}
	}, [currentItem, productID]);

	useEffect(() => {
		setPageLoading(false);
		sessionStorage.removeItem('summaryReloaded');
		sessionStorage.removeItem('calculatedMeasurements');

		document.addEventListener('scroll', onScrollMobile, { passive: true });
		document.addEventListener('resize', onScrollMobile, { passive: true });
		return () => {
			document.removeEventListener('scroll', onScrollMobile, { passive: true });
			document.removeEventListener('resize', onScrollMobile, { passive: true });
		};
	}, []);

	useEffect(() => {
		if (productBrand === 'Encompass by Pella') {
			setProductBrand('Encompass by Pella\u00AE');
		}
		if (productBrand === 'Pella Lifestyle Series') {
			setProductBrand('Pella\u00AE Lifestyle Series');
		}
		if (productBrand === 'Pella Impervia') {
			setProductBrand('Pella\u00AE Impervia\u00AE');
		}
		if (productBrand === 'Pella 250 Series') {
			setProductBrand('Pella\u00AE 250 Series');
		}
	}, [productBrand]);

	useEffect(() => {
		const unit = itemHeading?.includes('Pella® Lifestyle Series') ? 0.25 : 0.125;
		setIncrementUnit(unit);
	}, [itemHeading, productID]);

	useEffect(() => {
		if (productType === 'stock-window' || productType === 'entry-door') {
			setImageDisclaimer(errorData?.find((item) => item.non_field_id === 'Interior Image Disclaimer')?.error);
		}
		if ((productType === 'sash' || productType === 'screen') && getSessionStorage('calculatedMeasurements')) {
			const calcs = getSessionStorage('calculatedMeasurements');
			setMeasureCalc(calcs);
			if (calcs?.actual?.width) {
				setMeasureStatus('calculated');
				setItemEdited(true);
			}
		} else if (productType !== 'sash' && productType !== 'screen') {
			setMeasureStatus('manual');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [productType]);

	useEffect(() => {
		if (image450 === true) {
			setDrawingSize(450);
		} else if (image400 === true) {
			setDrawingSize(400);
		} else if (image350 === true) {
			setDrawingSize(350);
		} else {
			setDrawingSize(250);
		}
	}, [image350, image400, image450]);

	useEffect(() => {
		function imageViewToggle() {
			drawingView === 'exterior' && updateDrawingView(configKey, 0, drawingSize);
			drawingView === 'interior' && updateDrawingView(configKey, 1, drawingSize);
		}

		imageViewToggle();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- need to fix later, outside of current scope
	}, [configKey, drawingSize, drawingView]);

	useEffect(() => {
		if (typeof locations.ecommerce === 'boolean' && locations.ecommerce !== isEcommerce) {
			setIsEcommerce(locations.ecommerce);
		}
	}, [isEcommerce, locations.ecommerce]);

	useEffect(() => {
		if (typeof locations.parts_only === 'boolean' && isPartsOnly !== locations.parts_only) {
			setIsPartsOnly(locations.parts_only);
		}
	}, [isPartsOnly, locations.parts_only]);

	useEffect(() => {
		if (locations?.service_info?.branch_region === 'west') {
			setIsEastCoast(false);
		} else {
			setIsEastCoast(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [isEastCoast, JSON.stringify(locations.service_info)]);

	useEffect(() => {
		if (profile.isPro && !isPro) {
			setClientID('H04');
			setIsPro(true);
			if (!loadConfiguration) {
				setLoadConfiguration(true);
			}
		} else if (!profile.isPro && (isPro || isPro === null)) {
			setClientID('H03');
			setIsPro(false);
			if (!loadConfiguration) {
				setLoadConfiguration(true);
			}
		}
	}, [profile.isPro, isPro, loadConfiguration]);

	// ways to load a config
	useEffect(() => {
		const editItemConfiguration = async (quoteID, itemID, clientID, drawingSize) => {
			const item = await getLineItemConfig(quoteID, itemID, clientID, drawingSize, productID);
			if (item?.data?.success === true) {
				setCurrentItem(item.data);
				setLoading(false);
			} else {
				setTag('Pella Component', 'Configure', 'API Transaction ID', item?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('getLineItemConfig');
				setContext('Error', {
					errorData: JSON.stringify(item?.data) || 'Unknown Error Data',
					responseURL: item?.request?.responseURL,
					configData: item?.config?.data,
					quoteID: quoteID,
					clientID: clientID,
					itemID: itemID,
				});
				captureMessage(item?.data?.error?.message || 'Unknown Error Details');

				setError(item?.data?.error);
				setLoading(false);
			}
		};

		const loadConfig = async (configKey) => {
			const item = await getCurrentConfigurationState(configKey);
			if (item?.data?.success === true) {
				setCurrentItem(item.data);
				setLoading(false);
				setPageLoading(false);
			} else if (!sessionStorage.getItem('configureReloaded')) {
				sessionStorage.setItem('configureReloaded', true);
				window.location.reload();
			} else {
				setTag('Pella Component', 'Configure', 'API Transaction ID', item?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('getCurrentConfigurationState');
				setContext('Error', {
					errorData: JSON.stringify(item?.data) || 'Unknown Error Data',
					responseURL: item?.request?.responseURL,
					configData: item?.config?.data,
					initialConfigKey: initialConfigKey,
					updatedConfigKey: updatedConfigKey,
					usedConfigKey: configKey,
				});
				captureMessage(item?.data?.error?.message || 'Unknown Error Details');

				setError(item?.data?.error);
				setLoading(false);
				setPageLoading(false);
			}
		};

		const loadItemFromQueryParams = async (productID, clientID, drawingSize, filters) => {
			const item = await loadItem(productID, clientID, drawingSize, filters);
			if (item?.data?.success === true) {
				setCurrentItem(item.data);
				setLoading(false);
			} else {
				setTag('Pella Component', 'Configure', 'API Transaction ID', item?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('loadItem - from query params');
				setContext('Error', {
					errorData: JSON.stringify(item?.data) || 'Unknown Error Data',
					responseURL: item?.request?.responseURL,
					configData: item?.config?.data,
					productID: productID,
					clientID: clientID,
					filters: filters,
				});
				captureMessage(item?.data?.error?.message || 'Unknown Error Details');

				setError(item?.data?.error);
				setLoading(false);
			}
		};

		const initialProductConfiguration = async (productID, clientID, drawingSize, filters) => {
			setGettingInitialConfig(true);
			const item = await loadItem(productID, clientID, drawingSize, filters);
			if (item?.data?.success === true) {
				setCurrentItem(item.data);
				setGettingInitialConfig(false);
				setLoading(false);
			} else {
				setTag('Pella Component', 'Configure', 'API Transaction ID', item?.data?.REQUEST_ID);
				getCurrentScope().setTransactionName('loadItem - initial');
				setContext('Error', {
					errorData: JSON.stringify(item?.data) || 'Unknown Error Data',
					responseURL: item?.request?.responseURL,
					configData: item?.config?.data,
					productID: productID,
					clientID: clientID,
					filters: filters,
				});
				captureMessage(item?.data?.error?.message || 'Unknown Error Details');

				setError(item?.data?.error);
				setGettingInitialConfig(false);
				setLoading(false);
			}
		};

		if (typeof loadConfiguration === 'boolean' && typeof isPro === 'boolean' && drawingSize !== 0) {
			setLoadConfiguration('done');
			const queryParams = queryString.parse(window.location.search);
			if (!Object.keys(queryParams).length) {
				setQueryParamsError(true);
				return undefined;
			}
			let tempEditItem = localStorage.getItem('editingItem');
			if (!configKey && !error && !props?.location?.state?.configKey && tempEditItem) {
				tempEditItem = JSON.parse(tempEditItem);
				setEditItem(tempEditItem);
				setMeasureStatus('manual');
				removeLocalStorage('editingItem');
				if (tempEditItem?.src === 'cart') {
					editItemConfiguration(tempEditItem.quoteID, tempEditItem.itemID, clientID, drawingSize);
				}
				if (tempEditItem?.src === 'project') {
					editItemConfiguration(tempEditItem.projectID, tempEditItem.itemID, clientID, drawingSize);
				}
			} else if (props.location?.state?.configKey === configKey) {
				loadConfig(configKey);
			} else if (queryParams.options) {
				// Set clientID based on profile/state comparison. Fixes issue where logging in as a pro and refreshing resets pricing back to homeowner.
				const updateClientID = profile.isPro && !isPro ? 'H04' : 'H03';
				let filters = [];
				queryParams.options
					.split(',')
					.filter(Boolean)
					.map((questionsAnswers) => {
						const values = questionsAnswers.split('=');
						if (values.length !== 2) return null;
						filters.push({
							question_backend_name: values[0],
							answer_backend_name: values[1],
						});
					})
					.filter(Boolean);
				loadItemFromQueryParams(queryParams.id, updateClientID, drawingSize, filters);
			} else if (gettingInitialConfig === false) {
				// Set filters if 3D Modal options have been played with
				let filters = null;
				if (getSessionStorage('ar_config') != null) {
					filters = getSessionStorage('ar_config');
					removeSessionStorage('ar_config');
				}
				initialProductConfiguration(productID, clientID, drawingSize, filters);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [clientID, configKey, drawingSize, error, gettingInitialConfig, isPro, loadConfiguration, productID, props.location?.state?.configKey]);

	// process the currentItem
	useEffect(() => {
		if (Object.keys(currentItem).length > 0) {
			const filteredLimitations = currentItem.limitations?.filter((limitation) => limitation.hard_stop);
			setConfigKey(currentItem.config_key);
			setLimitations(filteredLimitations && filteredLimitations.length > 0 ? filteredLimitations : null);
			setDrawings(currentItem.drawings);
			if (currentDrawing && Object.keys(currentDrawing).length === 0) {
				setCurrentDrawing(currentItem.drawings.current_drawing);
			}
			setStockQty(currentItem.available_quantity);
			setUnorderedQuestions(currentItem.current_step_data.questions);
			currentItem?.current_step_data.questions && updateQueryParams(currentItem.current_step_data.questions);
			if (typeof window !== 'undefined') {
				const color = currentItem.specifications?.combined
					?.find((item) => item.key === 'Interior Finish')
					?.value?.replace(/ /g, '_')
					.toLowerCase();

				monetateDataLayer('setPageType', 'shop-configure');
				monetateDataLayer('addProductDetails', [{ productId: productID, sku: color }]);
			}
			if (filteredLimitations?.[0]) {
				window.scroll({ top: 0 });
			}
		}
	}, [currentDrawing, currentItem, productID]);

	useEffect(() => {
		if (!loading && branchRegion !== locations.service_info?.branch_region) {
			if (!branchRegion) {
				if (locations.service_info?.branch_region) {
					setBranchRegion(locations.service_info.branch_region);
				}
			} else {
				setLoading(true);
				setBranchRegion(locations.service_info?.branch_region);
				reconfigureConfiguration();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, [branchRegion, loading, locations?.service_info?.branch_region]);

	useEffect(() => {
		// update based on login
		if (!isLogged) return;

		setLoading(true);
		reconfigureConfiguration();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run on login, fix when possible
	}, [isLogged]);

	useEffect(() => {
		if (typeof window !== 'undefined' && drawingSize !== 0 && !productID) {
			setError('Unable to load item');
			setLoading(false);
		}
	}, [drawingSize, productID]);

	useEffect(() => {
		const matchType = ['part', 'screen', 'sash'];
		if (!loading && productID != null && Object.keys(currentItem).length > 0 && matchType.includes(productType)) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'productDetail',
				ecommerce: {
					detail: {
						products: [
							{
								id: productID,
								currency: 'USD',
								price: (currentItem.price?.amount * 0.01)?.toFixed(2) || '0.00',
								name: productID,
								category: productType.charAt(0).toUpperCase() + productType.slice(1),
							},
						],
					},
				},
			});
		}
	}, [productID, loading, currentItem, productType]);

	const reconfigureConfiguration = async () => {
		if (!configKey || drawingSize === 0 || editItem) {
			return;
		}

		let filters = [];
		configOptions
			?.split(',')
			.filter(Boolean)
			.map((questionsAnswers) => {
				const values = questionsAnswers.split('=');
				if (values.length !== 2) return null;
				filters.push({
					question_backend_name: values[0],
					answer_backend_name: values[1],
				});
			})
			.filter(Boolean);

		const item = await reconfigure(configKey, filters, drawingSize, profile.isPro ? 'H04' : 'H03');
		if (item?.data?.success === true) {
			setCurrentItem(item.data);
			setLoading(false);
		} else {
			setTag('Pella Component', 'Configure', 'API Transaction ID', item?.data?.REQUEST_ID);
			getCurrentScope().setTransactionName('reconfigure');
			setContext('Error', {
				errorData: JSON.stringify(item?.data) || 'Unknown Error Data',
				responseURL: item?.request?.responseURL,
				configData: item?.config?.data,
				productID: productID,
				clientID: clientID,
				filters: filters,
			});
			captureMessage(item?.data?.error?.message || 'Unknown Error Details');

			setError(item?.data?.error);
			setLoading(false);
		}
	};

	function updateQueryParams(questionsAnswers) {
		let queryStringOptions = {};
		setQuestionError(false);
		questionsAnswers.forEach((questionAnswer) => {
			if (questionAnswer.type === 'FreeEntry') {
				queryStringOptions[questionAnswer.question_text] = questionAnswer.selected_answer_id;
			} else if (questionAnswer.selected_answer_id) {
				queryStringOptions[questionAnswer.question_text] = questionAnswer.answers.find(({ id }) => id === questionAnswer.selected_answer_id)?.text;
			} else if (!questionAnswer.selected_answer_id && !questionAnswer.locked && !questionAnswer.using_different_answers) {
				setQuestionError(true);
				typeof window !== 'undefined' && window.scrollTo(0, 0);
			}
		});

		let optionsParams = '';
		Object.entries(queryStringOptions).forEach(([key, value]) => {
			optionsParams = optionsParams + `${key}=${value}` + ',';
		});

		let newParams = new URLSearchParams(window.location.search);
		newParams.set('options', optionsParams);
		let url = window.location.href.split('?')[0] + '?' + newParams.toString();
		setConfigOptions(new URLSearchParams(window.location.search).get('options'));
		history.pushState(null, '', url);
	}

	let updateAnswerTimer = null;
	const updateAnswerDelayed = (questionData, answerID) => {
		//special conditions to prevent prematurely submitting user updates on certain inputs
		clearTimeout(updateAnswerTimer);
		if (!loading && !(questionData.type === 'FreeEntry' && answerID.startsWith('0')) && !(questionData.type === 'FreeEntry' && answerID.length === 1)) {
			updateAnswerTimer = setTimeout(() => updateAnswer(questionData, answerID), answerID.endsWith('.') ? 2500 : 1500);
		}
	};

	const updateAnswer = (questionData, answerID) => {
		const answerIndex = questionData.answers.findIndex((item) => item.id === answerID);
		let answer = {};
		answer['selected_answer_id'] = questionData.type === 'FreeEntry' ? answerID.replace('-', '') : questionData.answers[answerIndex].id;
		answer['using_different_answers'] = questionData.using_different_answers;
		answer['unit_index'] = questionData.unit_index;
		answer['question_classification'] = questionData.question_classification;
		if (questionData.unit_index > 0) answer['allow_different_answers'] = true;

		if (measureStatus === 'calculated' && questionData.question_text === 'Hinged Glass Panel Option' && measureCalc?.original) {
			const newID = productID.replace(/(-screen|-sash)/, '');
			const results = getVisibleGlassMeasurements(newID, measureCalc.original.width, measureCalc.original.height, answerID === 'cbbb1966-d216-4a65-af32-cddd6428903b');
			if (results?.data?.width && results?.data?.height) {
				const widthQ = currentItem.current_step_data?.questions?.find((question) => question.question_text.match(/(frame|door\ call|call)\ width/i));
				const heightQ = currentItem.current_step_data?.questions?.find((question) => question.question_text.match(/(frame|door\ call|call)\ height/i));
				setMeasureCalc({ ...measureCalc, actual: { width: results.data.width.toString(), height: results.data.height.toString() } });
				updateAnswer(widthQ, results.data.width.toString());
				updateAnswer(heightQ, results.data.height.toString());
			}
		}

		const itemIndex = currentItem.current_step_data.questions.findIndex((item) => item.id === questionData.id);
		if (answerID !== '' && currentItem.current_step_data.questions[itemIndex].selected_answer_id !== answerID) {
			updateCurrentItem(questionData, answer);
		}
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'productClick',
			ecommerce: {
				click: {
					products: [
						{
							[questionData.question_text.replace(/ /g, '').toLowerCase()]: questionData.type === 'FreeEntry' ? answerID : questionData.answers[answerIndex].text,
						},
					],
				},
			},
		});
	};

	const updateCurrentItem = async (questionData, answer) => {
		setLoading(true);
		setError('');
		setItemEdited(true);
		const item = await updateItem(answer, questionData.id, configKey, clientID);
		if (item?.data?.success === true) {
			if (editItem && productType !== 'part' && productType !== 'stock-window') {
				setSessionStorage('userModified', true);
			}
			setCurrentItem(item.data);
			imageViewOverride(questionData, item.data.drawings);
			setTimeout(() => setLoading(false), 500); // help with updates
		} else {
			setTag('Pella Component', 'Configure', 'API Transaction ID', item?.data?.REQUEST_ID);
			getCurrentScope().setTransactionName('updateItem');
			setContext('Error', {
				errorData: JSON.stringify(item?.data) || 'Unknown Error Data',
				responseURL: item?.request?.responseURL,
				configData: item?.config?.data,
				answer: answer,
				questionID: questionData.id,
				clientID: clientID,
			});
			captureMessage(item?.data?.error?.message || 'Unknown Error Details');

			setError(item?.data?.error);
			setLoading(false);
			typeof window !== 'undefined' && window.scrollTo(0, 0);
		}
	};

	function imageViewOverride(question, drawing) {
		let interiorQuestions = ['Operation / Venting', 'Operation/Venting', 'Interior Color', 'Interior Finish', 'Hardware Finish'];
		let exteriorQuestions = ['Exterior Color', 'Exterior Finish'];
		if (drawing.available_drawings.length > 1 && interiorQuestions.includes(question.question_text)) {
			updateDrawingView(configKey, 1, drawingSize);
		} else if (drawing.available_drawings.length > 1 && exteriorQuestions.includes(question.question_text)) {
			updateDrawingView(configKey, 0, drawingSize);
		} else {
			setCurrentDrawing(drawing.current_drawing);
		}
	}

	const updateDrawingView = async (configKey, updateDrawingID, drawingSize) => {
		setUpdatingDrawing(true);
		const drawing = await updateDrawing(configKey, updateDrawingID, drawingSize);
		if (drawing?.data?.success === true) {
			setCurrentDrawing(drawing.data.drawing);
			setUpdatingDrawing(false);
		} else {
			setTag('Pella Component', 'Configure', 'API Transaction ID', drawing?.data?.REQUEST_ID);
			getCurrentScope().setTransactionName('updateDrawing');
			setContext('Error', {
				errorData: JSON.stringify(drawing?.data) || 'Unknown Error Data',
				responseURL: drawing?.request?.responseURL,
				configData: drawing?.config?.data,
			});
			captureMessage(drawing?.data?.error?.message || 'Unknown Error Details');

			setError(drawing?.data?.error);
			setUpdatingDrawing(false);
			typeof window !== 'undefined' && window.scrollTo(0, 0);
		}
	};

	useEffect(() => {
		if (productType !== null) {
			const index = questionsConfig?.question_order?.product?.findIndex((question) => question?.product_type === productType);
			setQuestionOrder(questionsConfig?.question_order?.product?.[index]?.question);
		}
	}, [questionsConfig?.question_order?.product, productType]);

	useEffect(() => {
		let orderedQuestions = [];
		let measurementQuestionsList = ['Frame Width', 'Door Call Width', 'Frame Height', 'Door Call Height', 'Jamb Extended Wall Depth', 'Overall Frame Width', 'Overall Frame Height'];
		let measurementQuestionsArray = [];
		let tempUnorderedQuestions = unorderedQuestions;

		// build new array with questions in proper order
		questionOrder?.forEach((item) => {
			if (measurementQuestionsList.some((question) => question === item)) {
				// seperate out measurement questions
				measurementQuestionsArray.push(unorderedQuestions.find((orderedQuestion) => orderedQuestion.question_text === item));
			} else {
				orderedQuestions.push(unorderedQuestions.find((orderedQuestion) => orderedQuestion.question_text === item));
			}

			tempUnorderedQuestions = tempUnorderedQuestions.filter((unorderedItem) => unorderedItem.question_text !== item);
		});

		// Measurement Question
		setMeasurementQuestions(measurementQuestionsArray.filter(Boolean));

		// add remaining unordered questions to end of array
		orderedQuestions = [...orderedQuestions, ...tempUnorderedQuestions];
		setQuestions(orderedQuestions.filter(Boolean));
	}, [questionOrder, unorderedQuestions]);

	// building breadcrumbs
	useEffect(() => {
		let deptPage = {};

		let breadcrumbLinksArray = [
			{
				link: [
					{
						title: 'Shop',
						href: '/shop/',
					},
				],
			},
		];

		// Dept Page
		if (productType === 'window') {
			deptPage = {
				link: {
					title: 'Windows',
					href: '/shop/windows/',
				},
			};
		} else if (productType === 'patio-door') {
			deptPage = {
				link: {
					title: 'Patio Doors',
					href: '/shop/doors/patio-doors/',
				},
			};
		} else if (productType === 'entry-door') {
			deptPage = {
				link: {
					title: 'Entry Doors',
					href: '/shop/doors/entry-doors/',
				},
			};
		} else if (productType === 'stock-window') {
			deptPage = {
				link: {
					title: 'Stock Windows',
					href: '/shop/windows/',
				},
			};
		} else if (productType === 'part') {
			deptPage = {
				link: { title: 'Parts', href: '/shop/parts/' },
			};
		} else if (productType === 'screen') {
			deptPage = {
				link: {
					title: 'Replacement Window Screens',
					href: '/shop/windows/parts/screens/',
				},
			};
		} else if (productType === 'sash') {
			deptPage = {
				link: {
					title: 'Replacement Window Sashes',
					href: '/shop/windows/parts/sashes/',
				},
			};
		}

		productType && breadcrumbLinksArray.push(deptPage);

		// PDP
		if (pdpURL && productName && pdpURL !== 'null' && productName !== 'null') {
			const urlObject = new URL(pdpURL);

			breadcrumbLinksArray.push({
				link: {
					title: productName,
					href: urlObject.pathname,
				},
			});
		}

		setPage_Links(breadcrumbLinksArray);
	}, [pdpURL, productName, productType]);

	const nextButtonHasError = () => {
		typeof window !== 'undefined' && window.scrollTo(0, 0);
	};

	// Bug Testing
	const initialConfigKey = props.location?.state?.configKey || '';
	const [updatedConfigKey, setUpdatedConfigKey] = useState('');
	useEffect(() => {
		setUpdatedConfigKey(configKey);
	}, [configKey]);

	function resetItem() {
		let newParams = new URLSearchParams(queryString.parse(window.location.search));
		newParams.set('options', '');
		history.pushState(null, '', window.location.href.split('?')[0] + '?' + newParams.toString());
		history.go(0);
		removeSessionStorage('calculatedMeasurements');
	}

	const updateQuantity = async (quantity) => {
		setUserModification(true);
		setItemQty(quantity);
	};

	function updateLoadingStatus(status) {
		setLoading(status);
	}

	function updateErrorMessaging(error) {
		setError(error);
	}

	const onModalClose = (e) => {
		e.preventDefault();
		setModalOpen(null);
	};

	const handleChange = (e) => {
		setModalError(null);
		setZipValue(e.target.value);
	};

	const confirmZIPCode = (event) => {
		event.preventDefault();
		const validZip = isValidPostal(zipValue);
		if (!validZip) {
			setModalError('Please enter a valid ZIP code.');
		} else {
			setModalOpen(null);
			setModalError(null);
			setLocation(zipValue);
			removeSessionStorage('calculatedMeasurements');

			if (!getCookie('cartadd') && ['window', 'patio-door', 'entry-door'].includes(productType)) {
				const expire = new Date();
				setCookie('cartadd', 'configure true', new Date(expire.setDate(expire.getDate() + 60)), true);
			}

			navigate(`/shop/summary/?type=${productType}&name=${productName}&pdpURL=${pdpURL}&heading=${itemHeading}&itemID=${productID}&configOptions=${configOptions}&isPro=${isPro}${productType === 'screen' || productType === 'sash' ? `&brand=${productBrand}` : ''}`, {
				state: {
					editItem: editItem,
					configKey: configKey,
				},
			});
		}
	};

	const closeErrorAlert = () => {
		setZipValue(null);
		setModalError(null);
	};

	const checkZIPCode = () => {
		if (getLocalStorage('UserPostalCode') !== null) {
			removeSessionStorage('v');

			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: 'summaryClick',
				ecommerce: {
					click: {
						products: [
							{
								id: currentItem?.mpn || productID,
								name: productID,
							},
						],
					},
				},
			});

			if (!getCookie('cartadd') && ['window', 'patio-door', 'entry-door'].includes(productType)) {
				const expire = new Date();
				setCookie('cartadd', 'configure true', new Date(expire.setDate(expire.getDate() + 60)), true);
			}

			navigate(`/shop/summary/?type=${productType}&name=${productName}&pdpURL=${pdpURL}&heading=${itemHeading}&itemID=${productID}&configOptions=${configOptions}&isPro=${isPro}${productType === 'screen' || productType === 'sash' ? `&brand=${productBrand}` : ''}`, {
				state: {
					editItem: editItem,
					configKey: configKey,
				},
			});
		} else {
			setModalOpen('postalCode');
		}
	};

	const spaceReplacement = typeof window !== 'undefined' && document.querySelector(`.${styles.productCard}`)?.offsetHeight;
	const [stickyCard, setStickyCard] = useState(false);
	const [zoomEnabled, setZoomEnabled] = useState(false);
	const onScrollMobile = () => {
		if (typeof window !== 'undefined' && window.innerWidth < 768) {
			const target = document.querySelector(`.${styles.configureGrid_Main}`);

			if (window.scrollY > target?.offsetTop) {
				setStickyCard(true);
			} else {
				setStickyCard(false);
				setZoomEnabled(false);
			}
		} else {
			setStickyCard(false);
			setZoomEnabled(false);
		}
	};

	const onZoomBtnClick = () => setZoomEnabled(!zoomEnabled);

	const [measureStatus, setMeasureStatus] = useState(typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('showCalc') ? 'calculator' : null);
	const [measureCalc, setMeasureCalc] = useState({});
	const getCalculations = (actual, original) => setMeasureCalc({ original: original, actual: actual });

	function updateMeasureStatus(value) {
		setMeasureStatus(value);
	}

	useEffect(() => {
		const measureDiv = document.getElementById('js-measureDiv');
		if (measureCalc.actual && measureDiv) {
			window.scrollTo({ top: measureDiv.offsetTop - 100, behavior: 'smooth' });
		}
	}, [measureCalc.actual]);

	return (
		<Layout
			className='pb-6 md:pb-10'
			id='shop-configuration'
			pageTitle={pageTitle}
		>
			<Helmet>
				<title>{pageTitle} | Pella</title>
			</Helmet>
			{measureStatus !== 'calculator' && !pageLoading && isValidArray(page_links) && (
				<Breadcrumbs
					data={{
						active_page: 'Configurator',
						back_pages: page_links,
					}}
					currentUrl={window.location.pathname + window.location.search}
				/>
			)}
			{loading && <LoadingOverlay />}
			{queryParamsError && (
				<Container>
					<Alert
						id='alertFocus'
						aria-live='assertive'
						type='error'
						className='mb-4'
						hideClose
					>
						<div
							dangerouslySetInnerHTML={{
								__html: errorData?.find((item) => item.non_field_id === 'retrieve product info')?.error,
							}}
						/>
					</Alert>
				</Container>
			)}
			{/* Calculator */}
			{measureStatus === 'calculator' && (
				<CalculateMeasurements
					backendName={productID}
					item={currentItem}
					productType={productType}
					content={measureConfig.calculator}
					originalMeasurements={measureCalc.original}
					onClose={() => updateMeasureStatus(measureCalc?.actual ? 'calculated' : null)}
					getCalculations={getCalculations}
					sendAnswer={updateAnswer}
					setLoading={updateLoadingStatus}
					setMeasureStatus={updateMeasureStatus}
					errorData={errorData}
				/>
			)}
			{measureStatus !== 'calculator' && !queryParamsError && (
				<Container className={`${styles.configureGrid} mt-2 md:mt-6`}>
					<div className={styles.configureGrid_Sidebar}>
						<div className={styles.configureGrid_SidebarSticky}>
							{stickyCard ? <div style={{ height: spaceReplacement }} /> : ''}

							<div
								id='productCard'
								className={`${styles.productCard} ${stickyCard ? styles.productCard_sticky : 'relative'} ${zoomEnabled ? styles.productCard_zoomIn : ''} grid bg-white md:mb-3 md:p-4`}
							>
								<div className={`${styles.productCard_SmallBox} grid p-3 md:p-0`}>
									<button
										className={`${styles.productCard_ZoomBtn} ${!stickyCard ? 'hidden' : ''} absolute`}
										onClick={onZoomBtnClick}
										aria-label='Zoom In/Out'
									>
										<Icon
											name={zoomEnabled ? 'zoom_in_map' : 'zoom_out_map'}
											className='text-sm'
										/>
									</button>

									<ImageViewer
										drawings={drawings}
										currentDrawing={currentDrawing}
										drawingSize={drawingSize}
										drawingView={[drawingView, setDrawingView]}
										updatingDrawing={updatingDrawing}
										imageDisclaimer={imageDisclaimer}
										unavailableImage={configData?.image_na}
									/>

									<div className='flex-row-reverse'>
										{itemHeading && (
											<p className='row-span-1 grid-cols-2 pr-4 text-xl font-bold leading-7 sm:mb-2.5 md:block'>
												{productBrand && <span className='mb-1 block font-bold uppercase'>{productBrand}</span>} {itemHeading}
											</p>
										)}

										{isEcommerce && currentItem?.current_step_data?.is_complete && (
											<Price
												priceHeading={isPro ? configData?.pro_price : null}
												price={currentItem?.price}
												priceComparison={currentItem?.price_comparison}
												className={styles.productCard_Price}
												priceAsText={configData?.price_text}
											/>
										)}
									</div>
								</div>

								<div
									id='productCardActions'
									className={`${styles.productCard_Actions} mt-3 md:mt-5`}
								>
									{!pageLoading && productType !== 'part' && productType !== 'stock-window' && (
										<Button
											onClick={configKey && !questionError ? checkZIPCode : nextButtonHasError}
											className={styles.configureNext_Btn}
											wide={true}
											tabIndex='0'
										>
											Review Build
										</Button>
									)}

									{editItem?.src !== 'project' && (
										<AddToProject
											currentItem={currentItem}
											limitations={limitations}
											userSignedIn={isLogged}
											configKey={configKey}
											clientID={clientID}
											fullName={itemHeading}
											configurationURL={typeof window !== 'undefined' ? window.location.href : null}
											loading={loading}
											className={styles.configureProjectCTA}
											redirectUrl={typeof window !== 'undefined' ? window.location.href : null}
										/>
									)}

									{!editItem && itemEdited && (
										<Button
											color='secondary'
											className={`${styles.productCard_Reset} mt-2.5 w-full`}
											onClick={() => resetItem()}
											wide
										>
											Reset To Default
										</Button>
									)}
								</div>

								{!pageLoading && isEcommerce && (productType === 'part' || productType === 'stock-window') && (
									<AddToCart
										currentItem={currentItem}
										updateQuantity={updateQuantity}
										userModification={userModification}
										detailsHeading={configData?.my_product_details}
										configKey={configKey}
										updateLoadingStatus={updateLoadingStatus}
										updateErrorMessaging={updateErrorMessaging}
										limitations={limitations}
										configurationURL={typeof window !== 'undefined' && window.location.href}
										editItem={editItem}
										backendName={productID}
										productType={productType}
										inventoryError={itemQty > stockQty}
										itemEdited={itemEdited}
										itemQty={itemQty}
										className={styles.productCard_AddToCart}
									/>
								)}

								{stockQty !== null && (stockQty <= 10 || itemQty > stockQty) && <p className={`${styles.productCard_Inventory} mt-2 p-2.5 text-center font-semibold uppercase text-white ${stockQty === 0 ? 'bg-red' : 'bg-black'}`}>{`${stockQty > 0 ? 'Only' : ''} ${stockQty} units left in this build!`}</p>}
							</div>

							{(productType === 'screen' || productType === 'sash') && configData?.screens_image_disclaimer && (
								<Alert
									type='info'
									className={`${styles.configureGrid_ImageDisclaimer} my-4`}
									hideClose
								>
									{configData.screens_image_disclaimer}
								</Alert>
							)}

							{isEcommerce && !pageLoading && !currentItem?.current_step_data?.is_complete && (
								<Alert
									type='info'
									className='mb-4'
									hideClose
								>
									<div
										dangerouslySetInnerHTML={{
											__html: errorData?.find((item) => item.non_field_id === 'price na')?.error,
										}}
									/>
								</Alert>
							)}
						</div>
						{error?.message && (
							<Alert
								type='error'
								className='mb-4'
								hideClose
							>
								{typeof error?.message === 'string' && <p>Error Message: {error?.message}</p>}
							</Alert>
						)}
					</div>

					<div className={styles.configureGrid_Main}>
						{!pageLoading && !configKey && error !== '' && typeof error?.message === 'string' && (
							<Alert
								id='alertFocus'
								aria-live='assertive'
								type='error'
								className='mb-4'
								hideClose
							>
								{error.message.includes('not offered in your area') ? (
									<div
										dangerouslySetInnerHTML={{
											__html: errorData?.find((item) => item.non_field_id === 'East West')?.error,
										}}
									/>
								) : (
									<p>{error.message}</p>
								)}
							</Alert>
						)}
						{!pageLoading &&
							limitations !== null &&
							0 in limitations &&
							limitations.map(
								(limitation, index) =>
									limitation && (
										<Alert
											id='alertFocus'
											aria-live='assertive'
											type='error'
											key={index}
											className='mb-4'
											hideClose
										>
											<p>{limitation.message}</p>
										</Alert>
									)
							)}
						{!pageLoading && questionError && (
							<Alert
								id='alertFocus'
								aria-live='assertive'
								type='error'
								className='mb-4'
								hideClose
							>
								<p>More detail needed: please update configuration options in red.</p>
							</Alert>
						)}

						{itemHeading && (
							<h1 className='hidden font-normal md:mb-2.5 md:block xl:mb-5'>
								{productBrand && <span className='mb-1 block text-base font-bold uppercase tracking-3px xl:text-xl'>{productBrand}</span>} {itemHeading}
							</h1>
						)}

						{questionsConfig.subheading && <p className='mb-4 text-lg font-semibold'>{questionsConfig.subheading}</p>}

						{itemQty > stockQty && (
							<Alert
								type='error'
								hideClose
								className='mb-4'
							>
								<div
									dangerouslySetInnerHTML={{
										__html: errorData?.find((item) => item.non_field_id === 'stockqty')?.error,
									}}
								/>
							</Alert>
						)}

						{/* Measurements */}
						{measurements && measurementQuestions.length > 0 && (
							<div
								id='js-measureDiv'
								className={styles.configureGrid_QuestionWrapper}
							>
								{measurements?.section_heading && <h2 className={styles.questionWrapper_Heading}>{measurements.section_heading}</h2>}
								{measurements?.subheading && <p className={styles.questionWrapper_Description}>{measurements.subheading}</p>}

								{measureStatus === 'calculated' && measureCalc?.actual?.width && (
									<CalculatorResults
										content={measureConfig?.calculator?.results}
										calculations={measureCalc.actual}
										setMeasureStatus={updateMeasureStatus}
									/>
								)}

								{measureStatus !== 'calculated' && measurements?.card?.[0] && (
									<Row className={`${styles.measureCalcBtns} mb-5`}>
										{measurements.card.map((card, index) => (
											<Column
												xs={12}
												xl={6}
												key={index}
												className='my-2.5'
											>
												<button
													className={`${index === 0 && measureStatus === 'manual' ? styles.activeBtn : ''} h-full w-full text-left`}
													onClick={() => updateMeasureStatus(index === 0 ? 'manual' : 'calculator')}
													aria-label='Calculate Measurements'
												>
													<CardRenderer
														data={{
															card_type: 'icon',
															description: card.body,
														}}
													/>
												</button>
											</Column>
										))}
									</Row>
								)}

								{(!measurements?.cards?.[0] || measureStatus === 'manual') &&
									measurementQuestions.map((question, index) => {
										const selectQuestions = ['Jamb Extended Wall Depth', 'Door Call Width', 'Door Call Height', 'Overall Frame Width'];
										const content = measurements.dimension[measurements.dimension.findIndex((measurement) => measurement.question_id === question.question_text)];

										if (selectQuestions.includes(question.question_text)) {
											return (
												<div
													id={`monetate-configure-mq${index}`}
													key={`${index}_${question.id}`}
													className={styles.configureGrid_AnswerWrapper}
												>
													<div
														aria-hidden={true}
														className={styles.answerWrapper_Image}
													>
														{!content?.icon?.url && <div className='w-16 sm:w-[100px] lg:w-[200px]' />}
														{content?.icon?.url && (
															<Image
																data={{
																	image: content.icon,
																	image_alt: '',
																}}
															/>
														)}
													</div>
													<div className={styles.answerWrapper_Content}>
														<p className={styles.answerWrapper_Heading}>{content.heading}</p>
														<p className={styles.answerWrapper_Description}>{content.description}</p>
														{content?.customizations?.[0] &&
															content.customizations.map((customize, index) => {
																if (customize.backend_name?.toLowerCase().includes(productID.toLowerCase())) {
																	return (
																		<div
																			key={index}
																			className={styles.questionWrapper_ProductSpecificContent}
																			dangerouslySetInnerHTML={{
																				__html: customize.description,
																			}}
																		/>
																	);
																}
															})}
													</div>
													<div className={styles.answerWrapper_AnswerInput}>
														<Row noGutters={true}>
															<Column className={styles.answerWrapper_InputCol}>
																<Select
																	id={content.heading}
																	labelText={content.box_values?.box_label}
																	defaultValue={question.answers?.find((answer) => answer?.id === question?.selected_answer_id)?.text}
																	value={question.answers?.length === 0 ? question.selected_answer_id : question.selected_answer_text}
																	onChange={(event) => updateAnswer(question, event.target.selectedOptions[0].id)}
																	groupClass={question?.answers?.length === 0 ? '-mt-4' : null}
																>
																	{question.answers?.length === 0 && <option value={question.selected_answer_id}>{question.selected_answer_id} inches</option>}
																	{question.answers.map((item, index) => (
																		<option
																			value={item.text}
																			id={item.id}
																			key={`${index}_${item.id}`}
																		>
																			{item.text}
																		</option>
																	))}
																</Select>
															</Column>
														</Row>
													</div>
												</div>
											);
										} else {
											return (
												<div
													id={`monetate-configure-mq${index}`}
													key={`${index}_${question.id}`}
													className={styles.configureGrid_AnswerWrapper}
												>
													<div
														aria-hidden={true}
														className={styles.answerWrapper_Image}
													>
														{isMulled && !content?.icon?.url && <div className='w-16 sm:w-[100px] lg:w-[200px]' />}
														{content.icon?.url && (
															<Image
																data={{
																	image: content.icon,
																	image_alt: '',
																}}
															/>
														)}
													</div>
													<div className={styles.answerWrapper_Content}>
														<p className={styles.answerWrapper_Heading}>{content.heading}</p>
														<p className={styles.answerWrapper_Description}>{content.description}</p>

														{content?.customizations?.[0] &&
															content.customizations.map((customize, index) => {
																if (customize.backend_name?.toLowerCase().includes(productID.toLowerCase())) {
																	return (
																		<div
																			key={index}
																			className={styles.questionWrapper_ProductSpecificContent}
																			dangerouslySetInnerHTML={{
																				__html: customize.description,
																			}}
																		/>
																	);
																}
															})}
													</div>
													<div className={styles.answerWrapper_AnswerInput}>
														<Row noGutters={true}>
															<Column className={styles.answerWrapper_InputCol}>
																<TextInput
																	id={content.heading}
																	labelText={content.box_values?.box_label}
																	assistiveText={`${incrementUnit}" ${content.box_values?.restrictions}`}
																	defaultValue={question.selected_answer_id}
																	onChange={(event) => updateAnswerDelayed(question, event.target.value)}
																	type='number'
																	groupClass={styles.answerWrapper_InputClass}
																	className='pr-16'
																	step={incrementUnit}
																	iconRight={<span className='text-sm font-semibold'>{content?.box_values?.input_unit}</span>}
																/>
															</Column>
														</Row>
													</div>
												</div>
											);
										}
									})}

								{(!measurements?.cards?.[0] || measureStatus === 'manual') && measurements?.need_help && (
									<div
										id='monetate-configure-needHelp'
										dangerouslySetInnerHTML={{
											__html: measurements.need_help,
										}}
									/>
								)}
							</div>
						)}
						{/* All Other Questions */}
						{questions.map((question, index) => {
							if (question.allow_different_answers && question.using_different_answers) return null;
							let csQuestion = [];

							questionsConfig.questions.filter((csQuestion) => csQuestion.question_id?.split(', ').includes(question.question_text)).length ? (csQuestion = questionsConfig.questions.filter((csQuestion) => csQuestion.question_id?.split(', ').includes(question.question_text))) : null;

							let answerType = csQuestion?.[0]?.answer_type || 'vertical';
							if (question.question_text === 'Package' && question.answers.length <= 1) return null;
							if (
								question.type === 'FreeEntry' &&
								question.answers.length === 0 &&
								question.answers.push({
									text: question.selected_answer_id,
								})
							);

							return (
								<React.Fragment key={`${index}_${question.id}`}>
									<div
										id={`monetate-configure-q-${question.id}`}
										className={`${questionOrder?.some((orderedQuestion) => orderedQuestion === question.question_text) ? '' : styles.nonOrderedQuestion} ${styles.configureGrid_QuestionWrapper} ${'selected_answer_id' in question && !question.selected_answer_id ? styles.configureGrid_QuestionWrapper_error : ''}`}
									>
										<h2 className={styles.questionWrapper_Heading}>{csQuestion[0]?.heading ? csQuestion[0].heading : question.question_text === 'Select Part' ? (productType === 'part' ? 'Select Part' : 'Window Size, Insulated Glass Type & Grille Type') : question.question_text}</h2>
										{csQuestion[0]?.description && (
											<div
												className={styles.questionWrapper_Description}
												dangerouslySetInnerHTML={{ __html: csQuestion[0].description }}
											/>
										)}

										{csQuestion[0]?.customization?.[0] &&
											csQuestion[0].customization.map((customize, index) => {
												if (productID.toLowerCase().includes(customize.brand) && customize.product.some((type) => productID.toLowerCase().includes(type))) {
													return (
														<div
															key={index}
															className={styles.questionWrapper_ProductSpecificContent}
															dangerouslySetInnerHTML={{
																__html: customize.description,
															}}
														/>
													);
												}
											})}

										{csQuestion[0]?.modal_link_text && (
											<button
												className={styles.questionWrapper_ModalLink}
												onClick={() => setModalOpen('configContent')}
											>
												{csQuestion[0].modal_link_text}
												<Icon name='keyboard_arrow_right' />
											</button>
										)}

										{answerType !== 'select' && (
											<div className={`${styles.answers} ${styles[`answerType_${answerType?.replace(/\s+/g, '')}`]}`}>
												{question.answers.map((answer, index) => {
													let csAnswer = {},
														customAnswer = {};

													csQuestion[0]?.answers.findIndex((csAnswer) => csAnswer.product_type.includes(productType) && csAnswer.answer_id.includes(answer.text)) >= 0 ? (csAnswer = csQuestion[0].answers[csQuestion[0].answers.findIndex((csAnswer) => csAnswer.product_type.includes(productType) && csAnswer.answer_id.includes(answer.text))]) : null;

													if (csAnswer?.customizations?.find((custom) => custom.brand || custom.product)) {
														customAnswer = csAnswer.customizations?.find((custom) => ((custom.brand && custom.brand?.some((brand) => productID.toLowerCase().includes(brand))) || !custom.brand) && ((custom.product && custom.product?.some((type) => productID.toLowerCase().includes(type.toLowerCase()))) || !custom.product));
													}

													return (
														<React.Fragment key={`${index}_${answer.id}`}>
															{answerType === 'swatch' && answer.id === question.selected_answer_id && <p className={`${styles.answerWrapper_Selected}`}>{answer.text}</p>}
															<label
																id={`monetate-configure-a-${answer.id}`}
																title={answerType === 'swatch' ? answer.text : null}
																className={`${styles.answer} ${answer.id === question.selected_answer_id ? styles.selectedAnswer : ''}`}
															>
																<input
																	className='sr-only'
																	type='radio'
																	name={question.question_text}
																	defaultChecked={answer.id === question.selected_answer_id}
																	onChange={() => updateAnswer(question, question.answers[index].id)}
																	id={`${answer.text}-${index}`}
																/>

																{answerType !== 'horizontal_no_image' && answerType !== 'vertical_no_image' && (
																	<>
																		{csAnswer.icon?.url ? (
																			<div
																				aria-hidden={true}
																				className={styles.answerWrapper_AnswerIcon}
																			>
																				<img
																					src={`${csAnswer.icon.url}${'?format=pjpg&auto=webp'}`}
																					alt={`${answer.text} icon`}
																				/>
																			</div>
																		) : (
																			<img
																				src={answer.image_url}
																				alt={`${answer.text} icon`}
																				aria-hidden={true}
																			/>
																		)}
																	</>
																)}

																{(csAnswer.heading || answer.text || csAnswer.description) && (
																	<div className={`${styles.answerWrapper_AnswerContent} text-black`}>
																		{!customAnswer?.heading && ('heading' in csAnswer && csAnswer.heading !== '' ? answerType !== 'swatch' && <p className='configuration-answerHeading text-subtitle-2 mb-1'>{csAnswer.heading}</p> : answerType !== 'swatch' && <p className={styles.answerWrapper_AnswerText}>{answer.text}</p>)}
																		{!customAnswer?.description && 'description' in csAnswer && csAnswer.description != '' ? <p className={`${styles.answerWrapper_AnswerDescription} leading-normal`}>{csAnswer.description}</p> : null}

																		{customAnswer?.heading && <p className='configuration-answerHeading text-subtitle-2 mb-1'>{customAnswer.heading}</p>}
																		{customAnswer?.description && (
																			<div
																				className={`${styles.answerWrapper_AnswerDescription} leading-normal`}
																				dangerouslySetInnerHTML={{ __html: customAnswer.description }}
																			/>
																		)}

																		{csAnswer.upgrade && <span className={styles.answerWrapper_AnswerUpgradeText}>Upgrade</span>}
																		{csAnswer.badge && (
																			<Badge
																				color='primary'
																				className='mt-2 !px-2 !py-0.5 text-sm/[17px] tracking-[0.1px] [&>*]:inline-flex [&>*]:items-center [&_span]:!mr-1 [&_span]:!text-sm'
																				dangerouslySetInnerHTML={{ __html: csAnswer.badge }}
																				renderAs='div'
																			/>
																		)}
																	</div>
																)}
															</label>
														</React.Fragment>
													);
												})}
											</div>
										)}
										{answerType === 'select' && (
											<Row>
												<Column
													xs={12}
													md={6}
													lg={12}
													xl={6}
												>
													<Select
														// was defaultValue
														value={question?.answers?.find((answer) => answer?.id === question?.selected_answer_id)?.text}
														onChange={(event) => updateAnswer(question, event.target.selectedOptions[0].id)}
														groupClass={styles.answerType_Select}
													>
														{question.answers.map((item, index) => {
															if ('available_quantity' in currentItem) {
																if (typeof item?.available === 'boolean') {
																	return (
																		<option
																			value={item.text}
																			id={item.id}
																			key={`${index}_${item.id}`}
																		>
																			{item.text}
																		</option>
																	);
																}
															} else {
																return (
																	<option
																		value={item.text}
																		id={item.id}
																		key={`${index}_${item.id}`}
																	>
																		{item.text}
																	</option>
																);
															}
														})}
													</Select>
												</Column>
											</Row>
										)}
									</div>
									{question.question_text === 'Select Part' && currentItem?.current_step_data?.item_number && (
										<div className={styles.configureGrid_QuestionWrapper}>
											<Row>
												<Column
													xs={12}
													md={6}
													lg={12}
													xl={6}
												>
													<h2 className={styles.questionWrapper_Heading}>Part Number</h2>
													<p>{currentItem?.current_step_data?.item_number}</p>
												</Column>
											</Row>
										</div>
									)}
								</React.Fragment>
							);
						})}
						{itemQty > stockQty && (
							<div
								className={styles.inventoryErrorMessage}
								dangerouslySetInnerHTML={{
									__html: errorData?.find((item) => item.non_field_id === 'inventory')?.error,
								}}
							/>
						)}
						{limitations !== null &&
							0 in limitations &&
							limitations.map(
								(limitation, index) =>
									limitation && (
										<Alert
											key={index}
											type='error'
											className='mb-4'
											hideClose
										>
											<p key={index}>{limitation.message}</p>
										</Alert>
									)
							)}
					</div>
				</Container>
			)}
			{questionsConfig.modal && (
				<Modal
					isOpen={modalOpen === 'configContent'}
					onClose={onModalClose}
				>
					{questionsConfig.modal.heading && <Modal.Header>{questionsConfig.modal.heading}</Modal.Header>}
					<Modal.Content>
						{questionsConfig.modal.option?.[0] &&
							questionsConfig.modal.option.map((item, index) => (
								<Row
									key={index}
									className={styles.modalRow}
								>
									{item.file && (
										<Column
											xs='auto'
											className={styles.modalRow_DesktopColumn}
										>
											<Image
												data={{
													image: item.file,
													image_alt: '',
												}}
											/>
										</Column>
									)}
									<Column xs='fluid'>
										{item.subheading && <p className={styles.modalSubheading}>{item.subheading}</p>}
										{item.description && (
											<div
												dangerouslySetInnerHTML={{
													__html: item.description,
												}}
												className={styles.modalDescription}
											/>
										)}
									</Column>
								</Row>
							))}
					</Modal.Content>
					{questionsConfig.modal.ctas?.[0] && (
						<Modal.Footer className={styles.modalFooter}>
							<CTA
								data={questionsConfig.modal.ctas[0]}
								onClick={onModalClose}
							/>
						</Modal.Footer>
					)}
				</Modal>
			)}
			<Modal
				isOpen={modalOpen === 'postalCode'}
				width={800}
				className={styles.postalCode_modal}
			>
				<Modal.Header>{locationConfig.build_location.heading}</Modal.Header>
				<Modal.Content>
					{modalError != null && (
						<Alert
							type='error'
							onClose={closeErrorAlert}
							className='mb-4'
						>
							{modalError}
						</Alert>
					)}

					<div
						className='mb-5'
						dangerouslySetInnerHTML={{
							__html: locationConfig.build_location.description,
						}}
					/>

					<form onSubmit={(event) => confirmZIPCode(event)}>
						<div className='flex flex-row flex-wrap items-end'>
							<TextInput
								id='zipCode'
								labelText='Zip Code'
								placeholder='ZIP Code'
								autoComplete='off'
								onChange={handleChange}
								groupClass='!m-0 w-full sm:w-auto'
							/>
							<Button
								id='speedcurve-zip-confirm'
								className='mt-2.5 sm:ml-2.5 sm:mt-0 sm:h-[54px] sm:py-1'
								type='submit'
							>
								Confirm Address
							</Button>
						</div>
					</form>
				</Modal.Content>
			</Modal>
			{!pageLoading && (
				<ExitIntent
					cookieName='configExitIntent'
					width={1200}
				>
					<div className='md:p-4'>
						{configData?.rta?.heading && <h2>{configData.rta.heading}</h2>}
						{configData?.rta?.subheading && <p className='mb-6 md:text-lg'>{configData.rta.subheading}</p>}

						<div id='monetate-rta-exit-content' />

						<ScheduleConsultation
							data={rtaData}
							location={window.location}
							layout='modal'
							referrer='configureExitModal'
						/>
					</div>
				</ExitIntent>
			)}
		</Layout>
	);
};

export default Configuration;

Configuration.propTypes = {
	location: PropTypes.object,
};

export const query = graphql`
	{
		contentstackLocationConfig {
			build_location {
				box_heading
				description
				heading
			}
		}
		contentstackConfiguratorMeasure {
			measure {
				product_type
				section_heading
				subheading
				card {
					horizontal
					body
				}
				dimension {
					question_id
					icon {
						url
						gatsbyImageData(quality: 90)
					}
					heading
					description
					customizations {
						backend_name
						description
					}
					input_type
					box_values {
						box_label
						customization {
							product
							box_label
						}
						input_unit
						restrictions
						min_max {
							brand
							product
							increments
						}
					}
				}
				need_help
			}
			calculator {
				heading
				subheading
				video_heading
				video {
					...Video
				}
				visible_glass {
					heading
					subheading
					box {
						box_id
						input_type
						box_heading
						unit
						increments
						trigger_text
						product_values {
							fe_name
							be_name
							product_type {
								fe_name
								be_name
							}
						}
						customization {
							backend_name
							instruction
						}
					}
					conversions {
						heading
						header_row
						conversion_data
					}
					need_help
				}
				calculate_actual_size
				results {
					icon
					heading
					subheading
					units
					width_height
					cta
				}
			}
		}
		contentstackQuestionsAnswers {
			subheading
			question_order {
				product {
					product_type
					question
				}
			}
			questions {
				question_id
				heading
				description
				customization {
					brand
					product
					description
				}
				modal_link_text
				answer_type
				answers {
					answer_id
					product_type
					icon {
						url
						gatsbyImageData(quality: 90)
					}
					heading
					description
					badge
					customizations {
						product
						brand
						heading
						description
					}
				}
			}
			modal {
				heading
				option {
					file {
						url
						gatsbyImageData(width: 100, quality: 90)
					}
					subheading
					description
				}
				ctas {
					...CTA
				}
			}
		}
		contentstackSummaryFields {
			errors {
				...Errors
			}
			pro_price
			price_text
			image_na {
				url
			}
			screens_image_disclaimer
			rta {
				heading
				subheading
			}
		}
	}
`;
